import { mapGetOrCreate } from "./mapGetOrCreate";


export const toMap = <T, K>(iterable: Iterable<T>, key: (t: T) => K): Map<K, T[]> => {
	const map = new Map<K, T[]>();
	for (const t of iterable) {
		mapGetOrCreate(map, key(t), () => []).push(t);
	}
	return map;
};

export const mapByKey = <T, K extends string & keyof T>(iterable: Iterable<T>, key: K): Map<T[K], T[]> => {
	const map = new Map<T[K], T[]>();
	for (const item of iterable) {
		mapGetOrCreate(map, item[key], () => []).push(item);
	}
	return map;
};

export const mapByTwoKeys = <T, OUTER_KEY extends string & keyof T, INNER_KEY extends string & keyof T>(
	iterable: Iterable<T>,
	outerKey: OUTER_KEY,
	innerKey: INNER_KEY
) => {
	const map = new Map<T[OUTER_KEY], Map<T[INNER_KEY], T[]>>();
	for (const item of iterable) {
		mapGetOrCreate(
			mapGetOrCreate(map, item[outerKey], () => new Map()),
			item[innerKey],
			() => []
		).push(item);
	}
	return map;
};


export const clusterBy = <T, K>(iterable: Iterable<T>, key: (t: T) => K): [K, T[]][] =>
	[...toMap<T, K>(iterable, key).entries()];


export const clusterByTwo = <T, OUTER_KEY, INNER_KEY>(
	iterable: Iterable<T>,
	toOuterKey: (e: T) => OUTER_KEY,
	toInnerKey: (e: T) => INNER_KEY
): [OUTER_KEY, [INNER_KEY, T[]][]][] =>
	clusterBy(iterable, toOuterKey).map(
		([outerKey, innerValues]) => ([
		outerKey,
		clusterBy(innerValues, toInnerKey)
	]));


export type Cluster<T extends object, K extends string & keyof T> =
	Record<K, T[K]> & Record<`for${Capitalize<K>}`, T[]>;

export type Cluster2<T extends object, OUTER_KEY extends string & keyof T, INNER_KEY extends string & keyof T> =
	Record<OUTER_KEY, T[OUTER_KEY]> &
	Record<`for${Capitalize<OUTER_KEY>}`, Cluster<T, INNER_KEY>[]>;

const capitalize = <S extends string>(s: S): Capitalize<S> => s.length < 1 ? '' as Capitalize<S>:
	`${s.charAt(0).toUpperCase()}${s.slice(1)}` as Capitalize<S>;

export const clusterByKey = <T extends object, K extends string & keyof T>(iterable: Iterable<T>, key: K): Cluster<T, K>[] =>
	[...toMap<T, T[K]>(iterable, o => o[key]).entries()].map(([k, v]) => ({ [key]: k, [`for${ capitalize(key) }`]: v } as Cluster<T, K>));

export const clusterByTwoKeys = <T extends object, OUTER_KEY extends string & keyof T, INNER_KEY extends string & keyof T>(
	iterable: Iterable<T>,
	outerKey: OUTER_KEY,
	innerKey: INNER_KEY
) => {
	return [...mapByTwoKeys(iterable, outerKey, innerKey).entries()].map(([outerKeysValue, innerMap])  => ({
		[outerKey]: outerKeysValue,
		[`for${capitalize(outerKey)}`]: [...innerMap.entries()].map(([innerKeysValue, innerArray]) => ({
			[innerKey]: innerKeysValue,
			[`for${capitalize(innerKey)}`]: innerArray
		} as Cluster<T, INNER_KEY>))
	} as Cluster2<T, OUTER_KEY, INNER_KEY>));
}
